<script setup>
import Clarity from "@microsoft/clarity";
import { usePage } from "@inertiajs/vue3";
import {
    dismissTrackingConsentBanner,
    hasTrackingConsent,
    isTrackingConsentBannerDismissed,
    setTrackingConsentAccepted,
} from "@/utils/trackingConsent";
import { computed, onMounted, ref, watch } from "vue";

const props = defineProps({
    hasBottomNav: {
        type: Boolean,
        default: false,
    },
});

const privacyUrl = typeof route === "function" ? route("privacy") : "/privacy";

const page = usePage();
const isVisible = ref(false);

const shouldSuppressBanner = computed(() => {
    const isUserProfilePage = page.component === "UserProfile/Index";
    const isPreviewOrScreenshot =
        page.props?.previewMode === true || page.props?.screenshotMode === true;

    return isUserProfilePage && isPreviewOrScreenshot;
});

const sendTrackingConsentToClarity = () => {
    try {
        Clarity.consent();
    } catch (_) {}

    try {
        Clarity.setTag("cookie_consent", "accepted");
    } catch (_) {}
};

const acceptCookies = () => {
    setTrackingConsentAccepted();
    sendTrackingConsentToClarity();
    isVisible.value = false;
};

const dismissBanner = () => {
    dismissTrackingConsentBanner();
    isVisible.value = false;
};

const syncBannerVisibility = () => {
    if (shouldSuppressBanner.value || hasTrackingConsent()) {
        if (hasTrackingConsent()) {
            sendTrackingConsentToClarity();
        }

        isVisible.value = false;
        return;
    }

    isVisible.value = !isTrackingConsentBannerDismissed();
};

onMounted(() => {
    syncBannerVisibility();
});

watch(shouldSuppressBanner, () => {
    syncBannerVisibility();
});

watch(
    () => page.url,
    () => {
        syncBannerVisibility();
    }
);
</script>

<template>
    <transition name="amo-cookie-banner-fade">
        <aside
            v-if="isVisible"
            class="amo-cookie-banner"
            :class="{
                'amo-cookie-banner--with-bottom-nav': hasBottomNav,
            }"
            role="dialog"
            aria-live="polite"
            aria-label="Cookie consent"
        >
            <div
                class="amo-cookie-banner__panel amo-card amo-card--rounded amo-card--padded"
            >
                <div class="amo-cookie-banner__copy">
                    <h2 class="amo-cookie-banner__title">Optional cookies</h2>
                    <p class="amo-cookie-banner__text">
                        We use cookies and similar technologies to understand
                        how the site is used and to improve our services.
                        <a :href="privacyUrl">Privacy policy</a>
                    </p>
                </div>

                <div class="amo-cookie-banner__actions">
                    <button
                        type="button"
                        class="amo-btn amo-btn--ghost amo-btn--sm"
                        @click="dismissBanner"
                    >
                        Not now
                    </button>
                    <button
                        type="button"
                        class="amo-btn amo-btn--primary amo-btn--sm"
                        @click="acceptCookies"
                    >
                        Accept cookies
                    </button>
                </div>
            </div>
        </aside>
    </transition>
</template>

<style scoped>
.amo-cookie-banner {
    position: fixed;
    left: 1rem;
    right: 1rem;
    bottom: calc(env(safe-area-inset-bottom, 0px) + 1rem);
    z-index: 1600;
    pointer-events: none;
}

.amo-cookie-banner--with-bottom-nav {
    bottom: calc(env(safe-area-inset-bottom, 0px) + 5.75rem);
}

.amo-cookie-banner__panel {
    display: flex;
    gap: 1.25rem;
    align-items: center;
    justify-content: space-between;
    max-width: 62rem;
    margin: 0 auto;
    padding: 1.25rem 1.5rem;
    background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0.96),
            rgba(245, 248, 255, 0.98)
        ),
        var(--background-primary);
    border: 1px solid rgba(22, 40, 70, 0.1);
    box-shadow: 0 20px 50px rgba(15, 23, 42, 0.14);
    pointer-events: auto;
}

.amo-cookie-banner__copy {
    max-width: 38rem;
    text-align: left;
}

.amo-cookie-banner__title {
    margin: 0;
    font-family: var(--font-title);
    font-size: 1.25rem;
    line-height: 1.1;
    color: var(--text-primary);
}

.amo-cookie-banner__text {
    margin: 0.35rem 0 0;
    color: var(--text-secondary);
    line-height: 1.55;
    font-size: var(--font-size-xs);
}

.amo-cookie-banner__text a {
    color: var(--primary);
    font-weight: 600;
    white-space: nowrap;
    text-underline-offset: 0.15em;
}

.amo-cookie-banner__actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: flex-end;
    flex-shrink: 0;
}

.amo-cookie-banner-fade-enter-active,
.amo-cookie-banner-fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.amo-cookie-banner-fade-enter-from,
.amo-cookie-banner-fade-leave-to {
    opacity: 0;
    transform: translateY(12px);
}

@media (max-width: 767px) {
    .amo-cookie-banner {
        left: 0.75rem;
        right: 0.75rem;
    }

    .amo-cookie-banner__panel {
        flex-direction: column;
        align-items: stretch;
        padding: 1rem;
    }

    .amo-cookie-banner__actions {
        justify-content: flex-end;
    }

    .amo-cookie-banner__actions .amo-btn--primary {
        flex: 1;
    }
}
</style>
